/**
 * The conversation with a forge: turn history, the live turn's stream, and
 * the screenshots + tool events it produces.
 *
 * NOT React Query: this is the WebSocket-driven part (docs/react-query-patterns.md
 * "Do NOT use React Query for"). Sending a turn is `POST /turns` → the
 * response's `requestId` → `useStreaming` subscribes; the runtime's events
 * come back as `token` / `activity` / `tool_call` / `tool_result` /
 * `forge_screenshot` / `forge_question` / `error` / `complete` frames on that id.
 *
 * A `forge_question` means Claude asked the owner (AskUserQuestion) and the
 * turn is waiting: `question` holds it until `answerQuestion` sends the
 * answer, and Claude then carries on in the same turn. A `forge_plan` is the
 * same for a plan (ExitPlanMode, ADR-023 A14): `plan` holds it until the
 * owner approves it or sends it back.
 *
 * Only the prompt is persisted server-side (forge_turns has no transcript),
 * so history renders each prior turn as the user's prompt plus a one-line
 * outcome bubble; the streamed assistant text lives for the page session.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import type { ForgePlanEventPayload, ForgeQuestionEventPayload } from '@/lib/websocket';
import { addBreadcrumb, captureError } from '@/lib/telemetry';
import { formatCredits } from '@/lib/format-credits';
import { forgeQueryKeys } from '../api/queryKeys';
import type {
  ForgeMessage,
  ForgePendingPlan,
  ForgePendingQuestion,
  ForgeScreenshot,
  ForgeToolEvent,
  ForgeTurnSummary,
} from '../types';

/** Screenshots kept per page session — older ones scroll off. */
const MAX_SCREENSHOTS = 24;
/** A turn's tool events kept in memory (Claude Code turns can run hundreds). */
const MAX_TOOL_EVENTS_PER_TURN = 400;

export interface UseForgeTurnResult {
  messages: ForgeMessage[];
  isProcessing: boolean;
  streamingText: string;
  activity: string | null;
  screenshots: ForgeScreenshot[];
  /** Screenshots that arrived since the user last opened the viewer. */
  unseenScreenshots: number;
  /** Tool events of the live turn. */
  pendingToolEvents: ForgeToolEvent[];
  /** Tool events of completed turns, keyed by the assistant bubble's timestamp. */
  toolEventHistory: Record<string, ForgeToolEvent[]>;
  lastFailure: { code: string; message: string; retryable: boolean } | null;
  /** The question Claude is waiting on, if any. */
  question: ForgePendingQuestion | null;
  /** Send the owner's answers (question text → answer). Throws if the platform refuses them. */
  answerQuestion: (answers: Record<string, string>) => Promise<void>;
  /** The plan Claude is waiting for the owner to approve, if any. */
  plan: ForgePendingPlan | null;
  /** Approve the plan: Claude leaves plan mode and builds. Throws if the platform refuses. */
  approvePlan: () => Promise<void>;
  /** Send the plan back with what to change: Claude keeps planning. */
  revisePlan: (feedback: string) => Promise<void>;
  /** Open a turn. `planMode`: Claude plans first and asks for approval before building. */
  sendTurn: (prompt: string, options?: { planMode?: boolean }) => Promise<void>;
  stopTurn: () => Promise<void>;
  retry: () => Promise<void>;
  clearScreenshots: () => void;
  markScreenshotsSeen: () => void;
}

/** The image type of a screenshot, from its file name (the runtime sends PNG, JPEG or WebP). */
export function screenshotMimeType(path: string): string {
  if (/\.jpe?g$/i.test(path)) return 'image/jpeg';
  if (/\.webp$/i.test(path)) return 'image/webp';
  return 'image/png';
}

/** How an answered question reads in the conversation. */
export function formatAnswers(
  question: ForgePendingQuestion,
  answers: Record<string, string>,
): string {
  return question.questions
    .map((q) => `${q.header || q.question}: ${answers[q.question] ?? ''}`)
    .join('\n');
}

/** How the owner's decision on a plan reads in the conversation. */
export function formatPlanDecision(approved: boolean, feedback: string): string {
  if (approved) return 'Approved the plan.';
  return feedback ? `Keep planning: ${feedback}` : 'Keep planning.';
}

function outcomeLine(turn: ForgeTurnSummary): string {
  // costCents is already in credits (1 credit = 1 cent).
  const cost = turn.costCents > 0 ? ` · ${formatCredits(turn.costCents)} credits` : '';
  const tools = turn.toolCalls > 0 ? ` · ${turn.toolCalls} tool calls` : '';
  switch (turn.status) {
    case 'completed':
      return `Turn completed${tools}${cost}.`;
    case 'stopped':
      return `Turn stopped${tools}${cost}.`;
    case 'failed':
      return `Turn failed${tools}${cost}.`;
    case 'expired':
      return `Turn expired (hit the wall-clock limit)${tools}${cost}.`;
    default:
      return 'Turn in progress…';
  }
}

export function useForgeTurn(forgeId: string | null): UseForgeTurnResult {
  const queryClient = useQueryClient();
  const authToken = authApi.getIdToken();

  const [requestId, setRequestId] = useState<string | null>(null);
  const [liveMessages, setLiveMessages] = useState<ForgeMessage[]>([]);
  const [activity, setActivity] = useState<string | null>(null);
  const [screenshots, setScreenshots] = useState<ForgeScreenshot[]>([]);
  const [unseenScreenshots, setUnseenScreenshots] = useState(0);
  const screenshotSeqRef = useRef(0);
  const [pendingToolEvents, setPendingToolEvents] = useState<ForgeToolEvent[]>([]);
  const [toolEventHistory, setToolEventHistory] = useState<Record<string, ForgeToolEvent[]>>({});
  const [lastFailure, setLastFailure] = useState<UseForgeTurnResult['lastFailure']>(null);
  const [isSending, setIsSending] = useState(false);
  const [question, setQuestion] = useState<ForgePendingQuestion | null>(null);
  const [plan, setPlan] = useState<ForgePendingPlan | null>(null);
  // Questions and plans answered (or dismissed by the turn ending) in this page
  // session: the turns query keeps reporting one as open until it refetches.
  const settledQuestionIdsRef = useRef<Set<string>>(new Set());
  // Characters of the live turn's stream already moved into a bubble (the
  // reply up to a question); the stream itself only resets between turns.
  const [streamOffset, setStreamOffset] = useState(0);
  const streamedCharsRef = useRef(0);

  const streamingTextRef = useRef('');
  const lastPromptRef = useRef<string | null>(null);
  // toolUseId → tool name, so a result bubble can be named (forge results
  // carry only the id; see websocket.ts ToolResultMessage).
  const toolNamesRef = useRef<Map<string, string>>(new Map());
  // Turns whose requestId we have already adopted from history (page reload
  // mid-turn) — never re-subscribe to the same one twice.
  const adoptedOpenTurnRef = useRef<string | null>(null);
  // Turns THIS page session opened. The reload pick-up below must never adopt
  // one: right after `complete` clears `requestId`, the turns query still says
  // `open` until its refetch lands, and adopting it re-subscribed to a stream
  // that had already ended — the page then showed the turn as running forever
  // (stop button, build and deploy disabled) with the streamed reply twice.
  const ownRequestIdsRef = useRef<Set<string>>(new Set());

  const turnsQuery = useQuery({
    queryKey: forgeQueryKeys.turns(forgeId ?? ''),
    queryFn: () => api.listForgeTurns(forgeId!),
    enabled: !!forgeId,
  });
  const turns = turnsQuery.data?.turns;

  // Reload mid-turn: an `open` turn in history is still streaming — subscribe
  // to its requestId so the page picks the stream back up. Its prompt moves
  // into the live conversation, as if sent from this page, so what streams
  // next (a reply, an answered question) follows it rather than landing
  // below the history's outcome line once the turn closes.
  useEffect(() => {
    if (!turns || requestId) return;
    const open = turns.find((t) => t.status === 'open');
    if (
      open &&
      adoptedOpenTurnRef.current !== open.requestId &&
      !ownRequestIdsRef.current.has(open.requestId)
    ) {
      adoptedOpenTurnRef.current = open.requestId;
      streamingTextRef.current = '';
      setLiveMessages((prev) =>
        prev.some((m) => m.role === 'user' && m.content === open.prompt)
          ? prev
          : [
              ...prev,
              {
                role: 'user',
                content: open.prompt,
                timestamp: open.startedAt ?? new Date().toISOString(),
              },
            ],
      );
      setRequestId(open.requestId);
    }
  }, [turns, requestId]);

  // Reload while Claude waits on a question or a plan: history carries it.
  useEffect(() => {
    if (!turns || question || plan) return;
    const open = turns.find((t) => t.status === 'open' && t.openQuestion);
    const waiting = open?.openQuestion;
    if (!open || !waiting || settledQuestionIdsRef.current.has(waiting.questionId)) return;
    if (waiting.kind === 'plan') {
      setPlan({ turnId: open.id, questionId: waiting.questionId, plan: waiting.plan });
    } else {
      setQuestion({
        turnId: open.id,
        questionId: waiting.questionId,
        questions: waiting.questions,
      });
    }
  }, [turns, question, plan]);

  const finishTurn = useCallback(
    (outcome: { failed?: { code: string; message: string } }) => {
      setActivity(null);
      setQuestion((q) => {
        if (q) settledQuestionIdsRef.current.add(q.questionId);
        return null;
      });
      setPlan((p) => {
        if (p) settledQuestionIdsRef.current.add(p.questionId);
        return null;
      });
      streamedCharsRef.current = 0;
      setStreamOffset(0);
      const stamp = new Date().toISOString();
      const text = streamingTextRef.current.trim();
      streamingTextRef.current = '';
      setPendingToolEvents((events) => {
        if (events.length > 0) setToolEventHistory((h) => ({ ...h, [stamp]: events }));
        return [];
      });
      if (text || outcome.failed) {
        setLiveMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: text || `Turn failed: ${outcome.failed?.message ?? 'unknown error'}`,
            timestamp: stamp,
          },
        ]);
      }
      setRequestId(null);
      setIsSending(false);
      // The turn row (status/cost/toolCalls) and the forge (lastActiveAt,
      // pendingCommand) both moved server-side.
      if (forgeId) {
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
      }
    },
    [forgeId, queryClient],
  );

  // Claude stopped to ask the owner something: what it said before asking
  // becomes its own bubble, so the conversation reads: reply so far →
  // question (or plan) → the owner's reply → the rest.
  const moveStreamToBubble = useCallback(() => {
    setActivity(null);
    const said = streamingTextRef.current.trim();
    if (said) {
      const stamp = new Date().toISOString();
      setLiveMessages((prev) => [...prev, { role: 'assistant', content: said, timestamp: stamp }]);
    }
    streamingTextRef.current = '';
    setStreamOffset(streamedCharsRef.current);
  }, []);

  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!authToken,
    // The activity line says what is happening while nothing else shows
    // (starting up, compacting, retrying a failed model call). Output means
    // it no longer applies, so a "retrying" line never outlives the recovery.
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
      streamedCharsRef.current += text.length;
      setActivity(null);
    }, []),
    onActivity: useCallback((label: string) => setActivity(label), []),
    onToolCall: useCallback((toolName: string, toolInput: unknown, toolUseId?: string) => {
      setActivity(null);
      if (toolUseId) toolNamesRef.current.set(toolUseId, toolName);
      setPendingToolEvents((events) =>
        [
          ...events,
          {
            type: 'tool_call' as const,
            timestamp: new Date().toISOString(),
            data: { toolName, toolInput, toolUseId },
          },
        ].slice(-MAX_TOOL_EVENTS_PER_TURN),
      );
    }, []),
    onToolResult: useCallback(
      (toolName: string, toolResult: unknown, isError: boolean, toolUseId?: string) => {
        const name = toolName || (toolUseId ? toolNamesRef.current.get(toolUseId) : undefined);
        setPendingToolEvents((events) =>
          [
            ...events,
            {
              type: 'tool_result' as const,
              timestamp: new Date().toISOString(),
              data: { toolName: name ?? 'tool', toolResult, isError, toolUseId },
            },
          ].slice(-MAX_TOOL_EVENTS_PER_TURN),
        );
      },
      [],
    ),
    onForgeScreenshot: useCallback(
      (payload: { requestId: string; path: string; contentBase64: string }) => {
        screenshotSeqRef.current += 1;
        const id = `shot-${screenshotSeqRef.current}`;
        setScreenshots((prev) =>
          [
            ...prev,
            {
              id,
              path: payload.path,
              dataUrl: `data:${screenshotMimeType(payload.path)};base64,${payload.contentBase64}`,
              requestId: payload.requestId,
              receivedAt: new Date().toISOString(),
            },
          ].slice(-MAX_SCREENSHOTS),
        );
        setUnseenScreenshots((n) => Math.min(n + 1, MAX_SCREENSHOTS));
      },
      [],
    ),
    onForgeQuestion: useCallback(
      (payload: ForgeQuestionEventPayload) => {
        if (settledQuestionIdsRef.current.has(payload.questionId)) return;
        moveStreamToBubble();
        setQuestion({
          turnId: payload.turnId,
          questionId: payload.questionId,
          questions: payload.questions,
        });
      },
      [moveStreamToBubble],
    ),
    onForgePlan: useCallback(
      (payload: ForgePlanEventPayload) => {
        if (settledQuestionIdsRef.current.has(payload.questionId)) return;
        moveStreamToBubble();
        setPlan({ turnId: payload.turnId, questionId: payload.questionId, plan: payload.plan });
      },
      [moveStreamToBubble],
    ),
    onError: useCallback(
      (code: string, message: string) => {
        captureError(new Error(message), { code, forgeId, surface: 'forge-turn' });
        // Transport-level failures are retryable by resending the same prompt;
        // a turn the platform closed as failed is not (the runtime already ran).
        const retryable = code !== 'FORGE_TURN_FAILED';
        setLastFailure({ code, message, retryable });
        if (!retryable) finishTurn({ failed: { code, message } });
      },
      [finishTurn, forgeId],
    ),
    onComplete: useCallback(() => {
      finishTurn({});
    }, [finishTurn]),
  });

  const sendTurn = useCallback(
    async (prompt: string, options: { planMode?: boolean } = {}) => {
      if (!forgeId || isSending || requestId) return;
      const trimmed = prompt.trim();
      if (!trimmed) return;
      setIsSending(true);
      setLastFailure(null);
      lastPromptRef.current = trimmed;
      streamingTextRef.current = '';
      streamedCharsRef.current = 0;
      setStreamOffset(0);
      streaming.reset();
      setLiveMessages((prev) => [
        ...prev,
        { role: 'user', content: trimmed, timestamp: new Date().toISOString() },
      ]);
      try {
        const result = await api.sendForgeTurn(forgeId, {
          prompt: trimmed,
          ...(options.planMode ? { planMode: true } : {}),
        });
        addBreadcrumb('forge', 'turn opened', {
          forgeId,
          turnId: result.turnId,
          planMode: options.planMode === true,
        });
        ownRequestIdsRef.current.add(result.requestId);
        setRequestId(result.requestId);
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send';
        setLastFailure({ code: 'SEND_FAILED', message, retryable: true });
        setIsSending(false);
      }
    },
    [forgeId, isSending, requestId, streaming, queryClient],
  );

  const answerQuestion = useCallback(
    async (answers: Record<string, string>) => {
      if (!forgeId || !question) return;
      await api.answerForgeQuestion(forgeId, question.turnId, {
        questionId: question.questionId,
        answers,
      });
      addBreadcrumb('forge', 'question answered', { forgeId, turnId: question.turnId });
      settledQuestionIdsRef.current.add(question.questionId);
      setQuestion(null);
      setLiveMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: formatAnswers(question, answers),
          timestamp: new Date().toISOString(),
        },
      ]);
    },
    [forgeId, question],
  );

  const decidePlan = useCallback(
    async (approved: boolean, feedback: string) => {
      if (!forgeId || !plan) return;
      await api.decideForgePlan(forgeId, plan.turnId, {
        questionId: plan.questionId,
        approved,
        ...(approved || !feedback ? {} : { feedback }),
      });
      addBreadcrumb('forge', approved ? 'plan approved' : 'plan sent back', {
        forgeId,
        turnId: plan.turnId,
      });
      settledQuestionIdsRef.current.add(plan.questionId);
      setPlan(null);
      // The plan stays in the conversation, followed by the decision.
      const stamp = new Date().toISOString();
      setLiveMessages((prev) => [
        ...prev,
        { role: 'assistant', content: plan.plan, timestamp: stamp },
        { role: 'user', content: formatPlanDecision(approved, feedback), timestamp: stamp },
      ]);
    },
    [forgeId, plan],
  );
  const approvePlan = useCallback(() => decidePlan(true, ''), [decidePlan]);
  const revisePlan = useCallback((feedback: string) => decidePlan(false, feedback), [decidePlan]);

  const stopTurn = useCallback(async () => {
    if (!forgeId) return;
    try {
      await api.stopForgeTurn(forgeId);
      addBreadcrumb('forge', 'stop requested', { forgeId });
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        forgeId,
        surface: 'forge-stop-turn',
      });
    }
  }, [forgeId]);

  const retry = useCallback(async () => {
    const prompt = lastPromptRef.current;
    if (!prompt) return;
    // Drop the optimistic user bubble the failed send left behind.
    setLiveMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'user' && last.content === prompt ? prev.slice(0, -1) : prev;
    });
    setIsSending(false);
    await sendTurn(prompt);
  }, [sendTurn]);

  const clearScreenshots = useCallback(() => {
    setScreenshots([]);
    setUnseenScreenshots(0);
  }, []);
  const markScreenshotsSeen = useCallback(() => setUnseenScreenshots(0), []);

  // History bubbles: every persisted turn as prompt + outcome, EXCEPT the one
  // currently open (its prompt is already in liveMessages, or it is being
  // picked up from a reload in which case there is no live bubble yet).
  const historyMessages = useMemo<ForgeMessage[]>(() => {
    if (!turns) return [];
    const liveStamps = new Set(liveMessages.map((m) => m.content));
    return turns
      .filter((t) => t.status !== 'open' || !liveStamps.has(t.prompt))
      .flatMap((t) => {
        const started = t.startedAt ?? new Date(0).toISOString();
        const out: ForgeMessage[] = [{ role: 'user', content: t.prompt, timestamp: started }];
        if (t.status !== 'open') {
          out.push({ role: 'assistant', content: outcomeLine(t), timestamp: t.endedAt ?? started });
        }
        return out;
      });
  }, [turns, liveMessages]);

  const messages = useMemo(() => {
    // History is ascending by start; live bubbles come after.
    const history = [...historyMessages].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    // A turn opened in this page session is in BOTH lists once the turns
    // query refetches — keep the live copy (it carries the streamed text).
    const liveUserPrompts = new Set(
      liveMessages.filter((m) => m.role === 'user').map((m) => m.content),
    );
    const dedupedHistory: ForgeMessage[] = [];
    for (let i = 0; i < history.length; i += 1) {
      const m = history[i];
      if (m.role === 'user' && liveUserPrompts.has(m.content)) {
        // Skip this prompt and its outcome line.
        if (history[i + 1]?.role === 'assistant') i += 1;
        continue;
      }
      dedupedHistory.push(m);
    }
    return [...dedupedHistory, ...liveMessages];
  }, [historyMessages, liveMessages]);

  const streamingText = (streaming.streamingText.forge ?? '').slice(streamOffset);

  return {
    messages,
    isProcessing: isSending || requestId !== null,
    streamingText,
    activity,
    screenshots,
    unseenScreenshots,
    pendingToolEvents,
    toolEventHistory,
    lastFailure,
    question,
    answerQuestion,
    plan,
    approvePlan,
    revisePlan,
    sendTurn,
    stopTurn,
    retry,
    clearScreenshots,
    markScreenshotsSeen,
  };
}
